import { useParams } from "react-router-dom";
import { ExternalLink, SearchX } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DetailList, DetailRow } from "@/components/detail-list";
import { EmptyState } from "@/components/empty-state";
import { NotificationStatusBadge } from "@/components/notification-status-badge";
import { DetailSkeleton } from "@/components/loading-skeletons";
import { PageHeader, PageTitle } from "@/components/layout/page-header";
import { useNotificationQuery } from "@/hooks/use-notifications";
import { cn, formatDateTime } from "@/lib/utils";

const BREADCRUMBS = [{ label: "Notifications", to: "/notifications" }];

interface StatCardProps {
  label: string;
  value: number;
  variant?: "default" | "success" | "error" | "info";
}

function StatCard({ label, value, variant = "default" }: StatCardProps) {
  const valueClassName =
    variant === "success"
      ? "text-emerald-600"
      : variant === "error"
        ? "text-red-600"
        : variant === "info"
          ? "text-blue-600"
          : "";

  return (
    <div className="rounded-lg bg-muted/60 px-4 py-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1 text-2xl font-semibold tracking-tight tabular-nums",
          valueClassName
        )}
      >
        {value}
      </p>
    </div>
  );
}

export function NotificationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const {
    data: notification,
    isLoading,
    error,
    refetch,
  } = useNotificationQuery(id!);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="Notification" />
        <DetailSkeleton />
      </div>
    );
  }

  if (error || !notification) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="Notification" />
        <EmptyState
          icon={SearchX}
          title="Notification Not Found"
          description="The notification you are looking for does not exist."
        >
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </EmptyState>
      </div>
    );
  }

  const stats = notification.delivery_stats;
  const progressPercentage = stats && stats.total > 0
    ? Math.round(((stats.created + stats.queued + stats.completed + stats.failed + stats.skipped) / stats.total) * 100)
    : 0;

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader breadcrumbs={BREADCRUMBS} title={notification.payload.title} />

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        <PageTitle title={notification.payload.title} />

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Notification Payload</CardTitle>
            </CardHeader>
            <CardContent>
              <DetailList>
                <DetailRow label="Title">
                  {notification.payload.title || "None"}
                </DetailRow>
                <DetailRow label="Message">{notification.payload.message}</DetailRow>
                <DetailRow label="Link">
                  {notification.payload.link ? (
                    <a
                      href={notification.payload.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                      title={notification.payload.link}
                    >
                      {notification.payload.link}
                      <ExternalLink className="size-3 shrink-0" />
                    </a>
                  ) : (
                    <span className="font-normal text-muted-foreground">None</span>
                  )}
                </DetailRow>
                <DetailRow label="Image">
                  {notification.payload.image_url ? (
                    <a
                      href={notification.payload.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block"
                      title={notification.payload.image_url}
                    >
                      <img
                        src={notification.payload.image_url}
                        alt="Notification image"
                        className="size-12 rounded-md object-cover ring-1 ring-black/5 transition-opacity hover:opacity-80"
                      />
                    </a>
                  ) : (
                    <span className="font-normal text-muted-foreground">None</span>
                  )}
                </DetailRow>
              </DetailList>
            </CardContent>
          </Card>

          <Card className="self-start">
            <CardHeader>
              <CardTitle>Notification Details</CardTitle>
            </CardHeader>
            <CardContent>
              <DetailList>
                <DetailRow label="Status">
                  <NotificationStatusBadge status={notification.status} />
                </DetailRow>
                <DetailRow label="Scheduled At">
                  {formatDateTime(notification.scheduled_at)}
                </DetailRow>
              </DetailList>
            </CardContent>
          </Card>

          {notification.delivery_stats && (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Device Delivery</CardTitle>
                <CardDescription>{progressPercentage}% processed devices</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  <StatCard
                    label="Created"
                    value={notification.delivery_stats.created}
                  />
                  <StatCard
                    label="Queued"
                    value={notification.delivery_stats.queued}
                    variant="info"
                  />
                  <StatCard
                    label="Failed"
                    value={notification.delivery_stats.failed}
                    variant="error"
                  />
                  <StatCard
                    label="Completed"
                    value={notification.delivery_stats.completed}
                    variant="success"
                  />
                  <StatCard
                    label="Skipped"
                    value={notification.delivery_stats.skipped}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
